import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/RichTextEditor';
import { Plus, ChevronLeft, ChevronRight, CalendarDays, Clock, Trash2, Bell, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';
import api from '@/lib/api';

const EVENT_TYPES = [
  'Reminder', 'Filing Due Date', 'Subfiling Due Date', 'Filing Expiration',
  'Subfiling Expiration', 'Out of Office', 'License/Certificate Expiration',
  'Project Start Date', 'Contract Service Due Diligence',
  'Contract Service Meeting', 'Other',
];

const REMINDER_OPTIONS = [
  { label: '1 day before', days: 1 },
  { label: '3 days before', days: 3 },
  { label: '1 week before', days: 7 },
  { label: '2 weeks before', days: 14 },
  { label: '30 days before', days: 30 },
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const typeColors = {
  'Reminder': 'bg-primary/20 text-primary border-primary/30',
  'Filing Due Date': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Subfiling Due Date': 'bg-amber-500/15 text-amber-300 border-amber-400/20',
  'Filing Expiration': 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  'Subfiling Expiration': 'bg-rose-500/15 text-rose-300 border-rose-400/20',
  'Out of Office': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  'License/Certificate Expiration': 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  'Project Start Date': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Contract Service Due Diligence': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'Contract Service Meeting': 'bg-cyan-500/15 text-cyan-300 border-cyan-400/20',
  'Other': 'bg-muted text-muted-foreground border-border',
};

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function CalendarView({ projectId = null }) {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({});
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayEvents, setDayEvents] = useState([]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const fetchEvents = useCallback(() => {
    const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDate = new Date(year, month + 1, 0);
    const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    let url = `/calendar/events?start=${start}&end=${end}`;
    if (projectId) url += `&project_id=${projectId}`;
    api.get(url).then(r => setEvents(r.data)).catch(() => {});
  }, [year, month, projectId]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/users').then(r => setUsers(r.data.filter(u => u.role !== 'Client'))).catch(() => {});
  }, []);

  const loadSections = async (pid) => {
    if (!pid) { setSections([]); return; }
    try {
      const r = await api.get(`/projects/${pid}/sections`);
      setSections(r.data);
    } catch { setSections([]); }
  };

  const openCreate = (date = null) => {
    setEditEvent(null);
    const defaultForm = {
      name: '', event_type: '', date: date || '', end_date: '', time: '', all_day: true,
      recurring: false, recurrence_pattern: '', recurrence_days: [],
      recurrence_day_of_month: '', recurrence_interval: '',
      notes: '', linked_project: projectId || '', linked_section: '', linked_pm: '',
      reminder_days: '',
    };
    setForm(defaultForm);
    if (projectId) loadSections(projectId);
    else setSections([]);
    setSheetOpen(true);
  };

  const openEdit = (ev) => {
    setEditEvent(ev);
    setForm({
      name: ev.name || '', event_type: ev.event_type || '', date: ev.date || '',
      end_date: ev.end_date || '',
      time: ev.time || '', all_day: ev.all_day !== false,
      recurring: !!ev.recurring, recurrence_pattern: ev.recurrence_pattern || '',
      recurrence_days: ev.recurrence_days || [], recurrence_day_of_month: ev.recurrence_day_of_month || '',
      recurrence_interval: ev.recurrence_interval || '',
      notes: ev.notes || '', linked_project: ev.linked_project || '',
      linked_section: ev.linked_section || '', linked_pm: ev.linked_pm || '',
      reminder_days: ev.reminder_days != null ? String(ev.reminder_days) : '',
    });
    if (ev.linked_project) loadSections(ev.linked_project);
    else setSections([]);
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return toast.error('Event name is required');
    if (!form.date) return toast.error('Date is required');
    if (!form.event_type) return toast.error('Event type is required');

    const payload = {
      ...form,
      reminder_days: form.reminder_days ? parseInt(form.reminder_days) : null,
      recurrence_day_of_month: form.recurrence_day_of_month ? parseInt(form.recurrence_day_of_month) : null,
      recurrence_interval: form.recurrence_interval ? parseInt(form.recurrence_interval) : null,
    };

    // Auto-set 30-day reminder for expiration types if not set
    if (!payload.reminder_days && (payload.event_type === 'Filing Expiration' || payload.event_type === 'Subfiling Expiration')) {
      payload.reminder_days = 30;
    }

    try {
      if (editEvent) {
        await api.put(`/calendar/events/${editEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/calendar/events', payload);
        toast.success('Event created');
      }
      setSheetOpen(false);
      fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async () => {
    if (!editEvent || !window.confirm(`Delete "${editEvent.name}"?`)) return;
    await api.delete(`/calendar/events/${editEvent.id}`);
    toast.success('Event deleted');
    setSheetOpen(false);
    setSelectedDay(null);
    fetchEvents();
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date().toISOString().slice(0, 10);

  // Build grid
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => {
      if (e.end_date && e.end_date > e.date) {
        return dateStr >= e.date && dateStr <= e.end_date;
      }
      return e.date === dateStr;
    });
  };

  const handleDayClick = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const evts = getEventsForDay(day);
    setSelectedDay(dateStr);
    setDayEvents(evts);
  };

  const toggleRecurrenceDay = (day) => {
    const current = form.recurrence_days || [];
    setForm({
      ...form,
      recurrence_days: current.includes(day)
        ? current.filter(d => d !== day)
        : [...current, day],
    });
  };

  // Pending type selection events
  const pendingEvents = events.filter(e => e.needs_type_selection && !e.event_type);

  return (
    <div className="space-y-4" data-testid="calendar-view">
      {/* Pending type selections */}
      {pendingEvents.length > 0 && (
        <div className="space-y-2">
          {pendingEvents.map(ev => (
            <div key={ev.id} className="flex items-center gap-3 p-3 rounded-md border border-amber-500/20 bg-amber-500/5" data-testid={`pending-event-${ev.id}`}>
              <AlertCircle className="h-4 w-4 text-amber-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{ev.name}</p>
                <p className="text-xs text-muted-foreground">{ev.date} · Please select an event type</p>
              </div>
              <Button size="sm" onClick={() => openEdit(ev)} className="bg-primary text-primary-foreground text-xs" data-testid={`set-type-${ev.id}`}>
                Set Type
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Calendar header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8 border-border" onClick={prevMonth} data-testid="cal-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-heading font-bold min-w-[160px] text-center">{monthName}</h2>
          <Button variant="outline" size="icon" className="h-8 w-8 border-border" onClick={nextMonth} data-testid="cal-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground ml-1" onClick={goToday}>Today</Button>
        </div>
        <Button onClick={() => openCreate(today)} className="bg-primary text-primary-foreground" data-testid="cal-new-event">
          <Plus className="h-4 w-4 mr-1" /> New Event
        </Button>
      </div>

      {/* Calendar grid */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="grid grid-cols-7">
          {WEEKDAYS.map(d => (
            <div key={d} className="p-2 text-center text-xs uppercase tracking-wider font-semibold text-muted-foreground border-b border-border bg-secondary/30">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (!day) return <div key={`empty-${i}`} className="min-h-[80px] border-b border-r border-border bg-secondary/10" />;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvts = getEventsForDay(day);
            const isToday = dateStr === today;
            const isSelected = dateStr === selectedDay;
            return (
              <div
                key={day}
                className={`min-h-[80px] border-b border-r border-border p-1 cursor-pointer transition-colors hover:bg-secondary/20 ${isSelected ? 'bg-primary/5 ring-1 ring-primary/30 ring-inset' : ''}`}
                onClick={() => handleDayClick(day)}
                data-testid={`cal-day-${day}`}
              >
                <div className={`text-xs font-medium mb-0.5 ${isToday ? 'bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center' : 'text-muted-foreground pl-1'}`}>
                  {day}
                </div>
                <div className="space-y-0.5">
                  {dayEvts.slice(0, 3).map(ev => (
                    <div
                      key={ev.id}
                      className={`text-xs px-1 py-0.5 rounded truncate border ${typeColors[ev.event_type] || typeColors['Other']}`}
                      onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                      title={ev.name}
                    >
                      {ev.name}
                    </div>
                  ))}
                  {dayEvts.length > 3 && <p className="text-xs text-muted-foreground pl-1">+{dayEvts.length - 3} more</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day detail panel */}
      {selectedDay && (
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-heading font-bold">{new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h3>
              <Button size="sm" variant="ghost" className="text-xs text-primary" onClick={() => openCreate(selectedDay)} data-testid="day-add-event">
                <Plus className="h-3 w-3 mr-0.5" /> Add Event
              </Button>
            </div>
            {dayEvents.length === 0 ? (
              <p className="text-xs text-muted-foreground">No events on this day</p>
            ) : (
              <div className="space-y-2">
                {dayEvents.map(ev => (
                  <div key={ev.id} className="flex items-start gap-2 p-2 rounded-md hover:bg-secondary/30 cursor-pointer transition-colors" onClick={() => openEdit(ev)} data-testid={`day-event-${ev.id}`}>
                    <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${(typeColors[ev.event_type] || '').split(' ')[0] || 'bg-muted'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{ev.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {ev.event_type && <Badge className={`text-xs ${typeColors[ev.event_type] || ''}`}>{ev.event_type}</Badge>}
                        {!ev.all_day && ev.time && <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" /> {ev.time}</span>}
                        {ev.project_name && <span className="text-xs text-muted-foreground">{ev.project_name}</span>}
                        {ev.reminder_days && <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Bell className="h-2.5 w-2.5" /> {Array.isArray(ev.reminder_days) ? ev.reminder_days.join(', ') + 'd' : ev.reminder_days + 'd'}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Event Form Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              {editEvent ? 'Edit Event' : 'New Event'}
            </SheetTitle>
            <SheetDescription>Calendar event details</SheetDescription>
          </SheetHeader>

          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Event Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="event-form-name" /></div>

            <div>
              <Label className="text-xs text-muted-foreground">Event Type *</Label>
              <Select value={form.event_type || '__none'} onValueChange={v => setForm({ ...form, event_type: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border" data-testid="event-form-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-60">
                  <SelectItem value="__none">Select...</SelectItem>
                  {EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-muted-foreground">Start Date *</Label><Input type="date" value={form.date || ''} onChange={e => setForm({ ...form, date: e.target.value })} className="bg-background border-border" data-testid="event-form-date" /></div>
              <div><Label className="text-xs text-muted-foreground">End Date</Label><Input type="date" value={form.end_date || ''} onChange={e => setForm({ ...form, end_date: e.target.value })} min={form.date || ''} className="bg-background border-border" data-testid="event-form-end-date" /></div>
            </div>
            {!form.all_day && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs text-muted-foreground">Time</Label><Input type="time" value={form.time || ''} onChange={e => setForm({ ...form, time: e.target.value })} className="bg-background border-border" data-testid="event-form-time" /></div>
                <div></div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label className="text-sm">All Day</Label>
              <Switch checked={form.all_day} onCheckedChange={v => setForm({ ...form, all_day: v, time: v ? '' : form.time })} data-testid="event-form-allday" />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-sm">Recurring</Label>
              <Switch checked={form.recurring} onCheckedChange={v => setForm({ ...form, recurring: v })} data-testid="event-form-recurring" />
            </div>

            {form.recurring && (
              <div className="space-y-2 border border-border rounded-md p-3 bg-background">
                <div>
                  <Label className="text-xs text-muted-foreground">Pattern</Label>
                  <Select value={form.recurrence_pattern || '__none'} onValueChange={v => setForm({ ...form, recurrence_pattern: v === '__none' ? '' : v })}>
                    <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="__none">Select</SelectItem>
                      <SelectItem value="Daily">Daily</SelectItem>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Annually">Annually</SelectItem>
                      <SelectItem value="Custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.recurrence_pattern === 'Weekly' && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Days of Week</Label>
                    <div className="flex gap-1 mt-1">
                      {WEEKDAYS.map(d => (
                        <button key={d} type="button"
                          className={`px-2 py-1 rounded text-xs border transition-colors ${(form.recurrence_days || []).includes(d) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-muted-foreground'}`}
                          onClick={() => toggleRecurrenceDay(d)}
                        >{d}</button>
                      ))}
                    </div>
                  </div>
                )}
                {form.recurrence_pattern === 'Monthly' && (
                  <div><Label className="text-xs text-muted-foreground">Day of Month</Label><Input type="number" min={1} max={31} value={form.recurrence_day_of_month || ''} onChange={e => setForm({ ...form, recurrence_day_of_month: e.target.value })} className="bg-background border-border w-24" placeholder="e.g. 15" /></div>
                )}
                {form.recurrence_pattern === 'Custom' && (
                  <div><Label className="text-xs text-muted-foreground">Every N weeks</Label><Input type="number" min={1} value={form.recurrence_interval || ''} onChange={e => setForm({ ...form, recurrence_interval: e.target.value })} className="bg-background border-border w-24" placeholder="e.g. 3" /></div>
                )}
              </div>
            )}

            {form.event_type === 'Other' && (
              <div><Label className="text-xs text-muted-foreground">Notes</Label><RichTextEditor value={form.notes || ''} onChange={v => setForm({ ...form, notes: v })} placeholder="Notes..." collapsedHeight={80} data-testid="event-form-notes" /></div>
            )}
            {form.event_type !== 'Other' && (
              <div><Label className="text-xs text-muted-foreground">Notes</Label><RichTextEditor value={form.notes || ''} onChange={v => setForm({ ...form, notes: v })} placeholder="Notes..." collapsedHeight={80} /></div>
            )}

            {/* Linked fields */}
            <div>
              <Label className="text-xs text-muted-foreground">Linked Project</Label>
              <SearchableSelect
                value={form.linked_project || ''}
                onValueChange={v => { setForm({ ...form, linked_project: v, linked_section: '' }); loadSections(v); }}
                options={[{ value: '', label: '-- None --' }, ...projects.map(p => ({ value: p.id, label: `${p.project_number} -- ${p.name}` }))]}
                placeholder="None"
                searchPlaceholder="Search projects..."
                data-testid="event-form-project"
              />
            </div>

            {form.linked_project && sections.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground">Linked Contract Section</Label>
                <Select value={form.linked_section || '__none'} onValueChange={v => setForm({ ...form, linked_section: v === '__none' ? '' : v })}>
                  <SelectTrigger className="bg-background border-border"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-60">
                    <SelectItem value="__none">-- None --</SelectItem>
                    {sections.map(s => <SelectItem key={s.id} value={s.id}>{s.section_number ? `${s.section_number} - ` : ''}{s.section_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs text-muted-foreground">Linked Project Manager</Label>
              <SearchableSelect
                value={form.linked_pm || ''}
                onValueChange={v => setForm({ ...form, linked_pm: v })}
                options={[{ value: '', label: '-- None --' }, ...users.map(u => ({ value: u.id, label: u.name }))]}
                placeholder="None"
                searchPlaceholder="Search people..."
                data-testid="event-form-pm"
              />
            </div>

            {/* Reminder */}
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Bell className="h-3 w-3" /> Reminder</Label>
              <Select value={form.reminder_days != null && form.reminder_days !== '' ? String(form.reminder_days) : '__none'} onValueChange={v => setForm({ ...form, reminder_days: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border" data-testid="event-form-reminder"><SelectValue placeholder="No reminder" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="__none">No reminder</SelectItem>
                  {REMINDER_OPTIONS.map(r => <SelectItem key={r.days} value={String(r.days)}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {(form.event_type === 'Filing Expiration' || form.event_type === 'Subfiling Expiration') && !form.reminder_days && (
                <p className="text-xs text-amber-400 mt-0.5">Default: 30-day reminder will be set automatically</p>
              )}
            </div>

            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground" data-testid="event-form-submit">
              {editEvent ? 'Update Event' : 'Create Event'}
            </Button>
            {editEvent && (
              <Button variant="outline" onClick={handleDelete} className="w-full border-destructive/30 text-destructive hover:bg-destructive/10 gap-1.5" data-testid="event-delete-btn">
                <Trash2 className="h-3.5 w-3.5" /> Delete Event
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
